import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserCheck } from 'lucide-react';
import { adminCompetitions, adminRunDraw, adminDeleteComp, adminUpdateComp, adminPendingWinners, api } from '@/lib/api';
import { formatGBP, formatDateShort } from '@/lib/format';
import WinnerContactModal from '@/pages/admin/WinnerContactModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/api/uploads/')) return `${BACKEND_URL}${u}`;
  return u;
};

const statusBadgeClass = (s) => {
  switch (s) {
    case 'LIVE': return 'bg-[color:var(--pd-accent-yellow)]/15 text-[color:var(--pd-accent-yellow)] border border-[color:var(--pd-accent-yellow)]/40';
    case 'COMING_SOON': return 'bg-[color:var(--pd-gold)]/15 text-[color:var(--pd-gold)] border border-[color:var(--pd-gold)]/40';
    case 'SELECTING_WINNER': return 'bg-[color:var(--pd-accent-orange)]/15 text-[color:var(--pd-accent-orange)] border border-[color:var(--pd-accent-orange)]/40';
    case 'DRAWN': return 'bg-[color:var(--pd-accent-yellow)]/10 text-[color:var(--pd-accent-yellow)] border border-[color:var(--pd-accent-yellow)]/30';
    case 'SOLD_OUT': return 'bg-white/5 text-muted-sage border border-pd';
    case 'WINNER_ANNOUNCED': return 'bg-white/5 text-[color:var(--pd-text)] border border-pd';
    case 'CANCELLED': return 'bg-[color:var(--pd-danger)]/10 text-[color:var(--pd-danger)] border border-[color:var(--pd-danger)]/40';
    default: return 'bg-white/5 text-muted-sage border border-pd';
  }
};

const AdminCompetitions = () => {
  const [items, setItems] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  // Pending winners keyed by competition_id, so a competition row can show a
  // "Pending winner" chip that reopens the modal. Also holds the modal state.
  const [pendingByComp, setPendingByComp] = useState({}); // { compId: { winner, draw, contact } }
  const [modalPayload, setModalPayload] = useState(null); // { winner, draw, contact, competitionTitle }
  const nav = useNavigate();

  const loadComps = () => adminCompetitions().then((d) => setItems(d.items || []));
  const loadPending = async () => {
    try {
      const d = await adminPendingWinners();
      const map = {};
      (d.items || []).forEach((it) => {
        if (it?.winner?.competition_id) map[it.winner.competition_id] = it;
      });
      setPendingByComp(map);
    } catch (e) { /* silently ignore */ }
  };
  useEffect(() => { loadComps(); loadPending(); }, []);

  const openPendingModal = (compId) => {
    const p = pendingByComp[compId];
    if (!p) return;
    const comp = items.find((c) => c.id === compId);
    setModalPayload({
      winner: p.winner,
      draw: p.draw,
      contact: p.contact,
      competitionTitle: comp?.title || p.winner?.prize || 'Competition',
    });
  };

  const runDraw = async (id) => {
    try {
      const r = await adminRunDraw(id);
      const comp = items.find((c) => c.id === id);
      setModalPayload({
        winner: r.winner,
        draw: r.draw,
        contact: r.contact,
        competitionTitle: comp?.title || 'Competition',
      });
      // Keep the row aware of the new pending winner even before we reload.
      setPendingByComp((m) => ({ ...m, [id]: { winner: r.winner, draw: r.draw, contact: r.contact } }));
      // Refresh so the status badge flips to DRAWN immediately.
      loadComps();
    } catch (err) {
      // Surface the actual backend detail so ambiguous "Draw failed" toasts
      // don't hide validation messages (e.g. "No eligible entries", "Winner
      // already drawn", "Competition isn't live yet").
      const detail = err?.response?.data?.detail;
      let msg = 'Draw failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || String(d)).join('; ');
      else if (err?.message) msg = err.message;
      toast.error(msg);
    }
    setConfirming(null);
  };

  const onModalConfirmed = (confirmedWinner) => {
    // Remove from pending set; refresh comp list so the LIVE→WINNER_ANNOUNCED status flip shows.
    if (confirmedWinner?.competition_id) {
      setPendingByComp((m) => {
        const next = { ...m }; delete next[confirmedWinner.competition_id]; return next;
      });
    }
    setModalPayload(null);
    loadComps();
  };

  const onModalRedrawn = (redrawResponse) => {
    // The backend voided the old winner AND ran a new draw; we now have a
    // fresh pending winner to review. Swap the modal payload in-place so the
    // admin can act on the new pick without closing / reopening.
    const compId = redrawResponse?.winner?.competition_id;
    if (compId) {
      setPendingByComp((m) => ({ ...m, [compId]: {
        winner: redrawResponse.winner, draw: redrawResponse.draw, contact: redrawResponse.contact,
      } }));
    }
    setModalPayload({
      winner: redrawResponse.winner,
      draw: redrawResponse.draw,
      contact: redrawResponse.contact,
      competitionTitle: modalPayload?.competitionTitle || 'Competition',
    });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this competition permanently?')) return;
    await adminDeleteComp(id);
    toast.success('Deleted');
    loadComps();
  };

  const toggleFeatured = async (c) => {
    setTogglingId(c.id);
    try {
      await adminUpdateComp(c.id, { featured: !c.featured });
      // Optimistic update
      setItems((arr) => arr.map((x) => x.id === c.id ? { ...x, featured: !c.featured } : x));
      toast.success(!c.featured ? 'Featured on homepage' : 'Removed from featured');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  const toggleStatus = async (c) => {
    const isLive = c.status === 'LIVE';
    const nextLabel = isLive ? 'Pause to Coming Soon' : 'Go live now';
    if (!window.confirm(`${nextLabel} — "${c.title}"?\n\nThis takes effect immediately for every visitor. Any scheduled go-live time will be cleared.`)) return;
    setTogglingId(c.id);
    try {
      const r = await api.post(`/admin/competitions/${c.id}/toggle-status`).then((res) => res.data);
      setItems((arr) => arr.map((x) => x.id === c.id ? { ...x, status: r.new_status, go_live_at: null } : x));
      toast.success(r.new_status === 'LIVE' ? 'Now live' : 'Paused to Coming Soon');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    } finally { setTogglingId(null); }
  };

  const featured = items.filter((c) => c.featured);

  return (
    <div data-testid="admin-competitions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Competitions</h1>
          <p className="text-sm text-muted-sage mt-1">
            {items.length} total · {items.filter((c) => c.status === 'LIVE').length} live · {featured.length} featured on homepage
          </p>
        </div>
        <button onClick={() => nav('/admin/competitions/new')} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2 font-medium" data-testid="admin-new-competition">
          + New competition
        </button>
      </div>

      {featured.length > 1 ? (
        <div className="mt-4 rounded-xl border border-[color:var(--pd-accent-yellow)]/50 bg-[color:var(--pd-accent-yellow)]/10 px-4 py-3 text-sm">
          <strong>Heads up:</strong> {featured.length} competitions are marked as Featured. Only the most recently updated LIVE one will appear as the hero on the homepage.
        </div>
      ) : null}

      {Object.keys(pendingByComp).length > 0 ? (
        <div
          className="mt-4 rounded-xl border border-[color:var(--pd-gold)]/60 bg-[color:var(--pd-gold)]/10 px-4 py-3 text-sm flex items-center justify-between gap-4"
          data-testid="pending-winners-banner"
        >
          <div>
            <strong className="text-[color:var(--pd-text)]">{Object.keys(pendingByComp).length} pending winner{Object.keys(pendingByComp).length === 1 ? '' : 's'}</strong>{' '}
            <span className="text-muted-sage">awaiting your confirmation. These are not yet visible on the public winners or past-draws pages.</span>
          </div>
          <button
            onClick={() => {
              const firstCompId = Object.keys(pendingByComp)[0];
              openPendingModal(firstCompId);
            }}
            className="text-xs bg-[color:var(--pd-gold)] text-[#14251c] rounded-lg px-3 py-1.5 font-medium hover:brightness-95 shrink-0"
            data-testid="pending-winners-banner-open"
          >
            Review
          </button>
        </div>
      ) : null}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-pd bg-[color:var(--pd-surface)]">
        <table className="w-full text-sm" data-testid="admin-competitions-table">
          <thead>
            <tr className="text-left font-mono text-[10px] text-muted-sage uppercase border-b border-pd">
              <th className="p-3">Competition</th>
              <th className="p-3">Status</th>
              <th className="p-3">Price</th>
              <th className="p-3">Tickets</th>
              <th className="p-3">Draw date</th>
              <th className="p-3">Featured</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const sold = c.tickets_sold ?? 0;
              const total = c.total_tickets ?? 0;
              const pct = total > 0 ? Math.min(100, Math.round((sold / total) * 100)) : 0;
              return (
                <tr key={c.id} className="border-t border-pd hover:bg-[color:var(--pd-bg)]/50">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-10 rounded-md overflow-hidden border border-pd bg-[color:var(--pd-bg)] shrink-0">
                        {c.hero_image_url ? (
                          <img src={resolveImg(c.hero_image_url)} alt="" className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-base truncate max-w-[260px]">{c.title}</div>
                        <div className="font-mono text-[10px] text-muted-sage truncate max-w-[260px]">
                          /{c.slug} · {c.category}
                          {c.instant_win ? <span className="ml-2 text-[color:var(--pd-accent-yellow)]">Instant-win</span> : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`inline-block px-2 py-0.5 rounded-md font-mono text-[10px] uppercase ${statusBadgeClass(c.status)}`}>{(c.status || '').replace(/_/g, ' ')}</span>
                      {(c.status === 'LIVE' || c.status === 'COMING_SOON') ? (
                        <button
                          onClick={() => toggleStatus(c)}
                          disabled={togglingId === c.id}
                          className="text-[10px] font-mono uppercase tracking-wide text-muted-sage hover:text-[color:var(--pd-text)] underline disabled:opacity-50"
                          data-testid={`admin-toggle-status-${c.id}`}
                        >
                          {c.status === 'LIVE' ? 'Pause to Coming Soon' : 'Go live now'}
                        </button>
                      ) : null}
                      {c.status === 'COMING_SOON' && c.go_live_at ? (
                        <span className="text-[10px] font-mono text-[color:var(--pd-gold)]" title={c.go_live_at}>
                          Auto-live {formatDateShort(c.go_live_at)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap">{formatGBP(c.ticket_price_gbp)}</td>
                  <td className="p-3 min-w-[140px]">
                    <div className="flex items-center justify-between text-xs">
                      <span>{sold} / {total}</span>
                      <span className="text-muted-sage">{pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-[color:var(--pd-bg)] overflow-hidden">
                      <div className="h-full bg-[color:var(--pd-accent-yellow)]" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap">{formatDateShort(c.draw_date)}</td>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(c)}
                      disabled={togglingId === c.id}
                      role="switch"
                      aria-checked={!!c.featured}
                      data-testid={`admin-featured-toggle-${c.id}`}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${c.featured ? 'bg-[color:var(--pd-accent-yellow)]' : 'bg-[color:var(--pd-bg)] border border-pd'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${c.featured ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2 justify-end">
                      {pendingByComp[c.id] ? (
                        <button
                          onClick={() => openPendingModal(c.id)}
                          className="border border-[color:var(--pd-gold)]/60 text-[color:var(--pd-gold)] rounded-lg px-2 py-1 text-xs inline-flex items-center gap-1 hover:bg-[color:var(--pd-gold)]/10"
                          data-testid={`admin-pending-winner-${c.id}`}
                          title={`Pending winner: ticket ${pendingByComp[c.id].winner.ticket_number}`}
                        >
                          <UserCheck size={12} /> Pending winner
                        </button>
                      ) : null}
                      <Link to={`/admin/competitions/${c.id}`} className="border border-pd rounded-lg px-2 py-1 text-xs hover:border-[color:var(--pd-accent-yellow)]" data-testid={`admin-edit-${c.id}`}>Edit</Link>
                      <Link to={`/admin/competitions/${c.id}/entries`} className="border border-pd rounded-lg px-2 py-1 text-xs hover:border-[color:var(--pd-accent-yellow)]">Entries</Link>
                      {(c.status === 'LIVE' || c.status === 'SELECTING_WINNER') && !pendingByComp[c.id] && (
                        confirming === c.id ? (
                          <>
                            <button onClick={() => runDraw(c.id)} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-lg px-2 py-1 text-xs font-medium" data-testid={`admin-confirm-draw-${c.id}`}>Confirm draw</button>
                            <button onClick={() => setConfirming(null)} className="border border-pd rounded-lg px-2 py-1 text-xs">Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => setConfirming(c.id)} className="border border-[color:var(--pd-accent-yellow)]/70 text-[color:var(--pd-accent-yellow)] rounded-lg px-2 py-1 text-xs" data-testid={`admin-run-draw-${c.id}`}>Run draw</button>
                        )
                      )}
                      <button onClick={() => remove(c.id)} className="border border-pd rounded-lg px-2 py-1 text-xs text-[color:var(--pd-danger)] hover:border-[color:var(--pd-danger)]">Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-sage">No competitions yet. Create the first one to get started.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <WinnerContactModal
        open={!!modalPayload}
        winner={modalPayload?.winner}
        draw={modalPayload?.draw}
        contact={modalPayload?.contact}
        competitionTitle={modalPayload?.competitionTitle}
        onConfirmed={onModalConfirmed}
        onRedrawn={onModalRedrawn}
        onClose={() => setModalPayload(null)}
      />
    </div>
  );
};
export default AdminCompetitions;
